import {
	Box,
	DialogContent,
	DialogTitle,
	Menu,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Typography,
} from '@mui/material';
import { FC, SyntheticEvent, useState, useRef, memo } from 'react';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { OrderCurrency } from 'ui/OrderCurrency';

export type TAmountSpeedDialProps = {
	merchandiseCost: number;
	deliveryCost: number;
	taxes: number;
	customDuties: number;
	easFee: number;
	customsProceduresFee: number;
	orderCurrency: string;
	thresholdCurrency: string;
	intrinsicThresholdExchangeRate: number;
	importThreshold: string;
};

export const AmountSpeedDial: FC<TAmountSpeedDialProps> = ({
	merchandiseCost,
	deliveryCost,
	taxes,
	customDuties,
	easFee,
	customsProceduresFee,
	orderCurrency,
	thresholdCurrency,
	intrinsicThresholdExchangeRate,
	importThreshold,
}) => {
	const anchorEl = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);

	const speedDialTooltipOpen = (e: SyntheticEvent) => {
		if (!anchorEl) return;
		setOpen(true);
		e.stopPropagation();
	};
	const speedDialTooltipClose = (e: SyntheticEvent) => {
		setOpen(false);
		e.stopPropagation();
	};

	return (
		<>
			<Box
				onClick={speedDialTooltipOpen}
				style={{ position: 'absolute', right: '0', top: '0', width: '100%', height: '100%' }}
				className="action-speed-dial"
				ref={anchorEl}
			/>
			<Menu
				anchorEl={anchorEl?.current}
				open={open}
				onClose={speedDialTooltipClose}
				sx={{
					'& .MuiPaper-root': {
						minWidth: '300px',
						maxWidth: 'none',
						borderRadius: '12px',
						p: 0,
						m: 0,
					},
					'& .MuiList-root': {
						padding: 0,
						m: 0,
					},
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
			>
				<Box>
					<DialogTitle color="primary" sx={{ p: '10px', fontSize: '1rem' }}>
						Amount Breakdown
					</DialogTitle>
					<DialogContent sx={{ p: '10px' }}>
						<TableContainer>
							<Table>
								<TableBody>
									<TableRow>
										<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
											Merchandise Cost:
										</TableCell>
										<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
											<AmountAndCurrency
												amount={merchandiseCost}
												currency={orderCurrency}
												size={0.9}
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '2px', borderBottom: 0 }}>Delivery Cost:</TableCell>
										<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
											<AmountAndCurrency
												amount={deliveryCost}
												currency={orderCurrency}
												size={0.9}
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '2px', borderBottom: 0 }}>Taxes:</TableCell>
										<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
											<AmountAndCurrency amount={taxes} currency={orderCurrency} size={0.9} />
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '2px', borderBottom: 0 }}>Customs Duties:</TableCell>
										<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
											<AmountAndCurrency
												amount={customDuties}
												currency={orderCurrency}
												size={0.9}
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
											Customs Procedures Fee:
										</TableCell>
										<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
											<AmountAndCurrency
												amount={customsProceduresFee}
												currency={orderCurrency}
												size={0.9}
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '2px', borderBottom: 0 }}>EAS Fee:</TableCell>
										<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
											<AmountAndCurrency amount={easFee} currency={orderCurrency} size={0.9} />
										</TableCell>
									</TableRow>

									<TableRow>
										<TableCell sx={{ padding: '2px', borderBottom: 0 }} />
										<TableCell sx={{ padding: '2px', borderBottom: 0 }} />
									</TableRow>
									<TableRow>
										<TableCell
											sx={{
												padding: '4px 2px 2px 2px',
												fontWeight: 'bold',
												borderBottom: 0,
												borderTop: '1px solid #016193',
											}}
										>
											<Typography color="primary" sx={{ fontWeight: 'bold' }}>
												Total:
											</Typography>
										</TableCell>
										<TableCell
											align="right"
											color="primary"
											sx={{
												padding: '4px',
												fontWeight: 'bold',
												borderBottom: 0,
												borderTop: '1px solid #016193',
											}}
										>
											<AmountAndCurrency
												amount={
													merchandiseCost +
													deliveryCost +
													taxes +
													customDuties +
													easFee +
													customsProceduresFee
												}
												currency={orderCurrency}
												size={0.9}
												bold
												color="primary"
											/>
										</TableCell>
									</TableRow>
									{+importThreshold > 0 && (
										<>
											<TableRow>
												<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
												<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
											</TableRow>

											<TableRow>
												<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
													Calculated Import Threshold:
													{/* ({orderCurrency}→ {thresholdCurrency}): */}
												</TableCell>
												<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
													<AmountAndCurrency
														amount={+importThreshold}
														currency={thresholdCurrency}
														size={0.9}
													/>
												</TableCell>
											</TableRow>
										</>
									)}
									{+importThreshold > 0 && orderCurrency !== thresholdCurrency && (
										<TableRow>
											<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
												Threshold Exchange Rate Applied:
												{/* ({orderCurrency}→ {thresholdCurrency}): */}
											</TableCell>

											<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
												<AmountAndCurrency
													amount={1}
													currency={orderCurrency}
													size={0.9}
													afterPoint={0}
												/>
												{/* 1 <OrderCurrency currency={orderCurrency} size={0.6} /> */} ={' '}
												<AmountAndCurrency
													amount={intrinsicThresholdExchangeRate}
													currency={thresholdCurrency}
													size={0.9}
													afterPoint={6}
												/>
												{/* {intrinsicThresholdExchangeRate}{' '}
												<OrderCurrency currency={thresholdCurrency} size={0.6} /> */}
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</DialogContent>
					{/* <DialogActions>
						<Button onClick={speedDialTooltipClose}>View order</Button>
						{/* <Button
						onClick={() => {
							if (!value[0] || !value[1]) return;
							handleDate({ from: value[0]?.toISODate(), to: value[1]?.toISODate() });
							setClose();
						}}
						autoFocus
						disabled={!value[0] || !value[1]}
					>
						Save
					</Button> 
					</DialogActions> */}
				</Box>
			</Menu>
		</>
	);
};

export const AmountSpeedDialMemo = memo(AmountSpeedDial);
